/**
 * createApiClientAuthAdapter.ts
 *
 * 工厂函数：将 @jinzhuan/api-client 创建的 typed client 适配为 AuthAdapter。
 *
 * 职责：
 * - 从 api-client 的 /api/v1/auth/me 端点读取当前会话
 * - 调用 /api/v1/auth/login 执行真实登录
 * - 调用 /api/v1/auth/logout 执行登出
 * - 将 MeResponse 转换为 AuthSession
 * - 管理 access token 的存储（refresh token 现在由 HttpOnly Cookie 自动管理）
 *
 * Issue #230 transient-failure contract:
 *   readSession() must distinguish "explicit unauthenticated" (401/403) from
 *   "transient backend failure" (network error, 5xx, malformed/HTML body from a
 *   load balancer). The former clears the session; the latter must not, or
 *   already-authenticated admins get bounced to /login when /auth/me flaps.
 */
import type {
  AuthAdapter,
  AuthSession,
  ReadSessionResult,
  SignInInput,
} from '@/contexts/AuthContext';
import {
  clearStoredAccessToken,
  getStoredAccessToken,
  storeAccessToken,
} from '@/lib/tokenStorage';
import { ApiError } from '@jinzhuan/api-client';
import type { LoginResponse, MeResponse } from '@jinzhuan/api-client';
// Issue #317: readSession() must run through the session-aware client so a
// stale-access-token 401 on /auth/me transparently refreshes and retries
// instead of bouncing the active admin to /login.
import { createSessionAwareApiClient } from '@/lib/createSessionAwareApiClient';
import {
  isIdleExpiredResponseBody,
  notifySessionIdleExpired,
  parseAuthErrorBody,
} from '@/lib/sessionExpirySignal';
import {
  clearStoredSessionIdentity,
  storeSessionIdentity,
} from '@/lib/sessionIdentityStorage';

/**
 * 安全契约（PR #119 / PR #184）：
 * - Refresh token 由 HttpOnly Cookie 管理，前端代码不感知、不存储。
 * - Access token 由 `tokenStorage` 模块统一存取；本文件不直接读 localStorage，
 *   避免键名漂移。`ErrorBoundary` 默认 reporter 共享同一存储，确保
 *   `/api/v1/error-reports` 能携带 `Authorization: Bearer`。
 */

export interface CreateApiClientAuthAdapterOptions {
  /** API 基础 URL，例如 'https://api.jinzhuan.example.com' 或 '/api' */
  baseUrl: string;
  /**
   * 可选的自定义 fetch 实现，主要用于测试环境注入 mock fetch。
   * 生产环境不传入此选项。
   */
  fetch?: typeof globalThis.fetch;
}

/**
 * 创建真实 AuthAdapter，基于 @jinzhuan/api-client typed client。
 */
export function createApiClientAuthAdapter(
  opts: CreateApiClientAuthAdapterOptions,
): AuthAdapter {
  // 创建 typed client，传入 token 获取钩子。
  // Issue #317: wraps via createSessionAwareApiClient so a 401 on /auth/me
  // (JWT access token expired but refresh-token row still alive) triggers
  // a single-flight /auth/refresh + replay instead of clearing the session.
  const client = createSessionAwareApiClient({
    baseUrl: opts.baseUrl,
    getAccessToken: () => getStoredAccessToken(),
    ...(opts.fetch ? { fetch: opts.fetch } : {}),
  });

  return {
    async readSession(): Promise<ReadSessionResult> {
      const token = getStoredAccessToken();
      // 无 token 时跳过远端请求 — 视为显式未登录，避免把首次访问当成 transient。
      if (!token) {
        return { status: 'unauthenticated' };
      }
      try {
        const { data } = await client.GET('/api/v1/auth/me');
        if (!data) {
          // 无 data 且未抛错：契约违例，视为 transient，保留 token 等待重试。
          return { status: 'transient_failure', reason: 'empty_body' };
        }
        storeSessionFromMe(data);
        return { status: 'authenticated', session: meResponseToAuthSession(data) };
      } catch (err) {
        return classifyReadSessionError(err);
      }
    },

    async signIn(input: SignInInput): Promise<AuthSession> {
      // 不在日志中打印 password（PII 安全红线）
      const { tenantSlug, email, password } = input;
      const { data, error } = await client.POST('/api/v1/auth/login', {
        body: { tenantSlug, email, password },
      });

      if (error) {
        throw new Error(
          typeof (error as { message?: string }).message === 'string'
            ? (error as { message: string }).message
            : 'Login failed',
        );
      }

      if (!data) {
        throw new Error('Login failed: empty response from server');
      }

      // 存储 access token (refresh token 现在由后端设置 HttpOnly Cookie)
      storeAccessToken(data.accessToken);
      // Issue #317: persist the tenant/org identity so the auto-refresh
      // helper can build the X-Tenant-Id / X-Org-Id headers that
      // /api/v1/auth/refresh requires. These ids are NOT secrets — they are
      // already exposed by /auth/me and /auth/login responses.
      storeSessionFromLogin(data);

      return loginResponseToAuthSession(data);
    },

    async signOut(): Promise<void> {
      try {
        // 调用 logout 端点，后端会从 __Host-jzc_rt HttpOnly Cookie 中读取 refresh token 并撤销。
        // Web 端不在 request body 中传递 refreshToken（body.refreshToken 字段为可选）。
        // 移动端（Expo）通过 body.refreshToken 传递（无 HttpOnly Cookie 支持）。
        await client.POST('/api/v1/auth/logout', { body: {} });
      } catch {
        // Swallow logout errors: we still clear local tokens below so the user
        // is signed out client-side even when the server call fails.
      } finally {
        clearStoredAccessToken();
        clearStoredSessionIdentity();
      }
    },
  };
}

function storeSessionFromLogin(data: LoginResponse): void {
  storeSessionIdentity({ tenantId: data.user.tenantId, orgId: data.user.orgId });
}

function storeSessionFromMe(data: MeResponse): void {
  storeSessionIdentity({ tenantId: data.tenantId, orgId: data.orgId });
}

/* ─────────────────────────────────────────────────────────────────── */
/* Error classification                                                */
/* ─────────────────────────────────────────────────────────────────── */

/**
 * Map a thrown error from openapi-fetch into the auth state machine.
 *
 * Rules (Issue #230 + Issue #289):
 * - 401 / 403            → explicit unauthenticated; clear session. When the
 *                          401 body indicates backend idle-timeout we ALSO
 *                          fire `sessionExpirySignal` so any other open
 *                          consumers (mounted hooks) drop their session view.
 * - 5xx                  → transient (LB/SLB or backend dep flap); KEEP session.
 * - Network / TypeError  → transient; KEEP session.
 * - Non-JSON body on 4xx → transient (likely HTML error page from upstream).
 * - Any other 4xx        → transient (conservative default — do not log out on a
 *                          generic 4xx; only an explicit auth failure should).
 */
export function classifyReadSessionError(err: unknown): ReadSessionResult {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) {
      maybeNotifyIdleExpired(err);
      return { status: 'unauthenticated' };
    }
    return { status: 'transient_failure', reason: `http_${err.status}` };
  }
  // TypeError from fetch typically signals network error / DNS failure.
  if (err instanceof TypeError) {
    return { status: 'transient_failure', reason: 'network_error' };
  }
  return { status: 'transient_failure', reason: 'unknown_error' };
}

function maybeNotifyIdleExpired(err: ApiError): void {
  const body = parseAuthErrorBody(err.bodyText);
  if (isIdleExpiredResponseBody(body)) {
    notifySessionIdleExpired('idle_expired');
  }
}

/* ─────────────────────────────────────────────────────────────────── */
/* Response conversion helpers                                         */
/* ─────────────────────────────────────────────────────────────────── */

function meResponseToAuthSession(data: MeResponse): AuthSession {
  return {
    subject: data.email,
    userId: data.id,
    // PR #117: 后端已显式提供 tenantId
    tenantId: data.tenantId,
    issuedAt: new Date().toISOString(),
    permissions: [...(data.permissions ?? [])],
    roles: [...(data.modules ?? [])],
    source: 'api-client',
  };
}

function loginResponseToAuthSession(data: LoginResponse): AuthSession {
  const user = data.user;
  return {
    subject: user.email,
    userId: user.id,
    // PR #117: 后端已显式提供 tenantId
    tenantId: user.tenantId,
    issuedAt: new Date().toISOString(),
    permissions: [...(user.permissions ?? [])],
    roles: [...(user.modules ?? [])],
    source: 'api-client',
  };
}
